import { createStyles, getStylesRef } from "@mantine/core"
import type { CSSObject, MantineColor, MantineTheme } from "@mantine/core"

import type { TileOrientation, TileSize, TileVariant } from "@/components/Tile/Tile"

export const sizes = {
    sm: {
        minWidth: "7.5rem",
        maxWidth: "12.5rem",
        padding: "1.25rem 1.5rem",
        gap: "1rem",
        fontSize: "0.875rem",
        "&& > svg": {
            height: "0.875rem",
            width: "0.875rem",
        },
    },
    md: {
        minWidth: "10rem",
        maxWidth: "15rem",
        padding: "1.25rem 1.5rem",
        gap: "1.25rem",
        fontSize: "1rem",
        "&& > svg": {
            height: "1rem",
            width: "1rem",
        },
    },
    lg: {
        minWidth: "12.5rem",
        maxWidth: "15rem",
        padding: "1.5rem 2rem",
        gap: "1.5rem",
        fontSize: "1.25rem",
        "&& > svg": {
            height: "1.25rem",
            width: "1.25rem",
        },
    },
    "horizontal-sm": {
        minWidth: "7.5rem",
        maxWidth: "12.5rem",
        padding: "1rem",
        gap: "0.75rem",
        fontSize: "0.875rem",
    },
    "horizontal-md": {
        minWidth: "7.5rem",
        maxWidth: "12.5rem",
        padding: "1.25rem 1.5rem",
        gap: "0.75rem",
        fontSize: "1rem",
    },
    "horizontal-lg": {
        minWidth: "10rem",
        maxWidth: "15rem",
        padding: "1.5rem",
        gap: "1rem",
        fontSize: "1.25rem",
    },
    "badged-horizontal-sm": {
        minWidth: "7.5rem",
        maxWidth: "12.5rem",
        padding: "1rem",
        gap: "0.5rem",
        fontSize: "0.875rem",
        "&& > svg": {
            height: "0.875rem",
            width: "0.875rem",
        },
    },
    "badged-horizontal-md": {
        minWidth: "10rem",
        maxWidth: "15rem",
        padding: "1.25rem 1.5rem",
        gap: "0.5rem",
        fontSize: "1rem",
        "&& > svg": {
            height: "1rem",
            width: "1rem",
        },
    },
    "badged-horizontal-lg": {
        minWidth: "12.5rem",
        maxWidth: "15rem",
        padding: "1.5rem",
        gap: "0.75rem",
        fontSize: "1.25rem",
        "&& > svg": {
            height: "1.25rem",
            width: "1.25rem",
        },
    },
}

const getSizeStyles = ({
    size,
    orientation,
    withBadge,
}: {
    size: TileSize
    orientation: TileOrientation
    withBadge: boolean
}): CSSObject => {
    if (orientation === "vertical") {
        return sizes[size]
    }

    if (withBadge) {
        return sizes[`badged-horizontal-${size}`]
    }

    return sizes[`horizontal-${size}`]
}

const getVariantStyles = ({
    variant,
    theme,
    color,
}: {
    variant: TileVariant
    theme: MantineTheme
    color: MantineColor
}): CSSObject => {
    switch (variant) {
        case "primary":
            return {
                color: theme.white,
                backgroundColor: theme.colors.violet[7],
                "&:hover": {
                    backgroundColor: theme.colors.violet[8],
                },
                "&:active:enabled": {
                    boxShadow: `0px 0px 0px 2px ${theme.colors.violet[5]}`,
                },
            }
        case "secondary":
        default:
            return {
                color: theme.colors.violet[8],
                backgroundColor: theme.colors.violet[1],
                "&:hover": {
                    backgroundColor: theme.colors.violet[2],
                },
                "&:active:enabled": {
                    boxShadow: `0px 0px 0px 2px ${theme.colors.violet[5]}`,
                },
            }
    }
}

const getOrientationStyles = ({ orientation }: { orientation: TileOrientation }): CSSObject => {
    if (orientation === "horizontal") {
        return {
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
        }
    }
    return {
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
    }
}

const getBadgeStyles = ({ orientation, size }: { orientation: TileOrientation; size: TileSize }): CSSObject => {
    const badgeOffset = {
        sm: {
            top: "0.375rem",
            right: "0.375rem",
        },
        md: {
            top: "0.5rem",
            right: "0.5rem",
        },
        lg: {
            top: "0.5rem",
            right: "0.5rem",
        },
    }

    const badgeSize = {
        sm: {
            padding: "0.0625rem 0.375rem",
        },
        md: {
            padding: "0.125rem 0.5rem",
        },
        lg: {
            padding: "0.125rem 0.5rem",
        },
    }

    const badgeFontSize = {
        sm: "0.75rem",
        md: "0.875rem",
        lg: "0.875rem",
    }

    if (orientation === "vertical") {
        return {
            position: "absolute",
            ...badgeOffset[size],
            "& > div": {
                ...badgeSize[size],
                height: "unset",
                textTransform: "unset",
                fontSize: badgeFontSize[size],
                "& > span": {
                    lineHeight: "150%",
                },
            },
        }
    }

    return {
        "& > div": {
            ...badgeSize[size],
            height: "unset",
            textTransform: "unset",
            fontSize: badgeFontSize[size],
            "& > span": {
                lineHeight: "150%",
            },
        },
    }
}

export interface TileStylesParams {
    size: TileSize
    withBadge: boolean
    orientation: TileOrientation
    variant: TileVariant
}

export const useStyles = createStyles((theme, { size, orientation, withBadge, variant }: TileStylesParams) => ({
    root: {
        ...getSizeStyles({ size, orientation, withBadge }),
        ...getVariantStyles({ variant, theme, color: theme.colors.violet[7] }),
        ...getOrientationStyles({ orientation }),
        position: "relative",
        border: "none",
        display: "inline-flex",
        borderRadius: "0.5rem",

        transition: "background-color 0.3s ease-out",

        "&:focus-visible": {
            outline: `0.125rem solid ${theme.fn.primaryColor()}`,
            outlineOffset: "0.125rem",
        },

        "&:disabled": {
            color: theme.colors.gray[5],
            backgroundColor: theme.colors.gray[1],
        },

        [`&:disabled .${getStylesRef("badge")} > div`]: {
            color: "white",
            backgroundColor: theme.colors.gray[5],
        },
    },
    label: {
        textAlign: "start",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontFamily: theme.fontFamily,
        fontWeight: 500,
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 1,
    },
    badge: {
        ref: getStylesRef("badge"),
        ...getBadgeStyles({ orientation, size }),
    },
}))
